import { create } from 'zustand';
import { MiniappError } from '@/api/miniapp/client';
import { miniappApi } from '@/api/miniapp/miniappApi';
import { useAuthStore } from './authStore';

/**
 * Сценарий регистрации по контракту FRONTEND_API.md.
 *
 * Доступен только когда authStore в фазе `registration`. Ведёт по шагам
 * email-кода, согласия, профиля и анкеты; каждый ответ бэкенда обновляет
 * фазу и шаг в authStore через applyResponse, поэтому переходы экранов
 * следуют за состоянием сервера, а не за локальной логикой.
 */

/** Подшаг ввода email: сначала запрос кода, потом его подтверждение. */
type EmailStage = 'enter_email' | 'enter_code';

interface RegistrationState {
  emailStage: EmailStage;
  email: string;
  /** Ошибка у поля почты или кода. */
  fieldError: string | null;
  submitting: boolean;
  /** Код можно запросить повторно (истёк или не пришёл). */
  canResend: boolean;

  setEmail(email: string): void;
  requestCode(): Promise<boolean>;
  confirmCode(code: string): Promise<boolean>;
  resetFieldError(): void;
  reset(): void;
}

/** Сообщение у поля по машиночитаемому типу ошибки. */
function fieldMessage(error: MiniappError): { message: string; canResend: boolean } {
  switch (error.type) {
    case 'REGISTRATION_USER_NOT_FOUND':
      return { message: 'Пользователь с такой почтой не найден.', canResend: false };
    case 'REGISTRATION_CODE_EXPIRED':
      return { message: 'Код истёк. Запросите новый.', canResend: true };
    case 'REGISTRATION_CODE_INVALID':
      return { message: 'Неверный код. Проверьте и попробуйте ещё раз.', canResend: true };
    case 'REGISTRATION_EMAIL_MISMATCH':
      return { message: 'Почта не совпадает. Запросите код заново.', canResend: true };
    default:
      return { message: error.message || 'Не удалось выполнить запрос.', canResend: true };
  }
}

export const useRegistrationStore = create<RegistrationState>((set, get) => ({
  emailStage: 'enter_email',
  email: '',
  fieldError: null,
  submitting: false,
  canResend: false,

  setEmail(email) {
    set({ email, fieldError: null });
  },

  resetFieldError() {
    set({ fieldError: null });
  },

  async requestCode() {
    const email = get().email.trim().toLowerCase();
    set({ submitting: true, fieldError: null });
    try {
      await miniappApi.requestEmailCode(email);
      set({ submitting: false, emailStage: 'enter_code', email, canResend: true });
      return true;
    } catch (error) {
      if (error instanceof MiniappError) {
        const { message } = fieldMessage(error);
        set({ submitting: false, fieldError: message });
      } else {
        set({ submitting: false, fieldError: 'Нет подключения к интернету.' });
      }
      return false;
    }
  },

  async confirmCode(code) {
    set({ submitting: true, fieldError: null });
    try {
      const response = await miniappApi.confirmEmailCode(get().email.trim().toLowerCase(), code.trim());
      // Ответ содержит новое состояние: applyResponse переведёт на следующий шаг
      useAuthStore.getState().applyResponse(response);
      set({ submitting: false });
      return true;
    } catch (error) {
      if (error instanceof MiniappError) {
        const { message, canResend } = fieldMessage(error);
        set({ submitting: false, fieldError: message, canResend });
      } else {
        set({ submitting: false, fieldError: 'Нет подключения к интернету.' });
      }
      return false;
    }
  },

  reset() {
    set({ emailStage: 'enter_email', email: '', fieldError: null, submitting: false, canResend: false });
  },
}));
